import { useGame } from "@/contexts/GameContext";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  CheckCircle,
  XCircle,
  SkipForward,
  Crown,
  Trophy,
  Award,
  Medal,
} from "lucide-react";
import ScoringRulesDialog from "@/components/game/ScoringRulesDialog";

export default function EnhancedPlayersList() {
  const { state } = useGame();

  const playersWithScores = state.players
    .map((player) => ({
      ...player,
      // Używaj pola points zamiast obliczania
      score: player.points || 0,
    }))
    .sort((a, b) => b.score - a.score);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />
          <h3 className="font-semibold text-white">
            Ranking Graczy ({state.players.length})
          </h3>
        </div>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {playersWithScores.map((player, rankIndex) => {
          const originalIndex = state.players.findIndex(
            (p) => p.id === player.id
          );
          const isCurrentPlayer = originalIndex === state.currentPlayerIndex;
          const isSelectedPlayer = originalIndex === state.selectedPlayerIndex;

          const getRankIcon = () => {
            switch (rankIndex) {
              case 0:
                return <Crown className="w-4 h-4 text-yellow-400" />;
              case 1:
                return <Medal className="w-4 h-4 text-slate-400" />;
              case 2:
                return <Award className="w-4 h-4 text-orange-400" />;
              default:
                return (
                  <span className="text-xs font-bold text-muted-foreground">
                    #{rankIndex + 1}
                  </span>
                );
            }
          };

          return (
            <div
              key={player.id}
              className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                isSelectedPlayer
                  ? "bg-green-600/20 border-2 border-green-400/50 shadow-md"
                  : isCurrentPlayer
                  ? "bg-purple-600/20 border-2 border-purple-400/50 shadow-md"
                  : "bg-white/5 border border-white/10"
              }`}
            >
              {/* Pozycja w rankingu */}
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                {getRankIcon()}
              </div>

              <Avatar
                className={`w-12 h-12 ${
                  isSelectedPlayer
                    ? "ring-2 ring-green-400"
                    : isCurrentPlayer
                    ? "ring-2 ring-purple-400"
                    : ""
                }`}
              >
                <AvatarFallback className="bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold">
                  {player.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-white">{player.name}</span>
                  {isSelectedPlayer && (
                    <Badge className="bg-green-500 text-white text-xs">
                      Wykonuje
                    </Badge>
                  )}
                  {isCurrentPlayer && !isSelectedPlayer && (
                    <Badge className="bg-purple-500 text-white text-xs">
                      Kręci
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1 text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    <span>{player.tasksCompleted}</span>
                  </div>
                  <div className="flex items-center gap-1 text-red-400">
                    <XCircle className="w-3 h-3" />
                    <span>{player.tasksSkipped}</span>
                  </div>
                  <div className="flex items-center gap-1 text-yellow-400">
                    <SkipForward className="w-3 h-3" />
                    <span>{player.skipsUsed}/2</span>
                  </div>
                </div>
              </div>

              {/* Wynik */}
              <div className="text-right">
                <div className="text-lg font-bold text-white">
                  {player.score}
                </div>
                <div className="text-xs text-white/50">punktów</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center">
        <ScoringRulesDialog />
      </div>
    </div>
  );
}
